import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, FileText, Activity, Pill, AlertCircle } from 'lucide-react';
import medicalRecordService from '../../services/medicalRecordService';
import Cie10Autocomplete from '../common/Cie10Autocomplete';
import DocumentDownloadMenu from '../common/DocumentDownloadMenu';
import useHistoriaDraft from '../../hooks/useHistoriaDraft';
import { PRESENTATIONS, ROUTES } from './sections/medicationOptions';
import PreviousLabResultsSection from './sections/PreviousLabResultsSection';
import RequestedTestsSection from './sections/RequestedTestsSection';
import SickLeaveSection from './sections/SickLeaveSection';
import { toBogotaIso, fromIsoToDateInput } from './sections/dateUtils';

// Calcula la clasificacion de IMC segun rangos estandar de la OMS
function getBmiCategory(bmiValue) {
  if (bmiValue === null || bmiValue === undefined || isNaN(bmiValue)) return null;
  if (bmiValue < 18.5) return { label: 'Bajo peso', color: 'text-blue-700 bg-blue-50 border-blue-200' };
  if (bmiValue < 25) return { label: 'Peso normal', color: 'text-green-700 bg-green-50 border-green-200' };
  if (bmiValue < 30) return { label: 'Sobrepeso', color: 'text-yellow-700 bg-yellow-50 border-yellow-200' };
  if (bmiValue < 35) return { label: 'Obesidad grado I', color: 'text-orange-700 bg-orange-50 border-orange-200' };
  if (bmiValue < 40) return { label: 'Obesidad grado II', color: 'text-red-700 bg-red-50 border-red-200' };
  return { label: 'Obesidad grado III (morbida)', color: 'text-red-900 bg-red-100 border-red-300' };
}

// El formulario guarda cada código CIE-10 como objeto ({ code, ... }, lo que
// entrega Cie10Autocomplete) y el submit lee `.code`. El backend los devuelve
// como string ('R51'): sin normalizar, al editar se enviaban null y la historia
// perdía sus códigos. Acepta ambas formas.
function toCodeObject(value) {
  if (!value) return null;
  if (typeof value === 'string') return { code: value };
  if (typeof value === 'object' && value.code) return { code: value.code };
  return null;
}

// Fila de medicamento en blanco. presentation y route son listas cerradas: ''
// significa "sin elegir" y el PDF lo imprime como dato no registrado.
const emptyMedication = () => ({
  name: '', dosage: '', presentation: '', route: '', frequency: '', duration: ''
});

// Días de incapacidad para el envío. Habilitada, se manda lo escrito (el backend
// valida y explica el error). Apagada se conserva solo si el esquema lo aceptaría
// (entero entre 1 y 180): un 200 olvidado en una sección apagada haría fallar el
// guardado de toda la historia por un dato que ni siquiera se imprime.
function sickLeaveDays({ enabled, days }) {
  if (days === '' || days === undefined || days === null) return undefined;
  const n = Number(days);
  if (enabled) return n;
  return Number.isInteger(n) && n >= 1 && n <= 180 ? n : undefined;
}

const emptyLabRow = () => ({ name: '', date: '', result: '' });

// Estado del formulario al abrirlo: vacío (con el motivo de la cita) al crear,
// o a partir de la historia guardada al editar. Es la base con la que se
// comparan los cambios sin guardar del borrador local.
function buildFormData(appointment, initialData) {
  if (!initialData) {
    return {
      // SECCIÓN 1: MOTIVO DE CONSULTA
      chiefComplaint: appointment?.reason || '',
      chiefComplaintCode: null, // Código CIE-10 asociado

      // SECCIÓN 2: ENFERMEDAD ACTUAL
      currentIllness: '',

      // SECCIÓN 3: ANTECEDENTES
      pathologicalHistory: '',

      // SECCIÓN 4: EXAMEN FÍSICO (Vitales + Recuadro libre)
      weight: '',
      height: '',
      bloodPressure: '',
      heartRate: '',
      temperature: '',
      oxygenSaturation: '',
      physicalExamNotes: '',

      // RESULTADOS DE EXÁMENES PREVIOS (opcional, apagada por defecto)
      previousLabResults: { enabled: false, items: [emptyLabRow()], notes: '' },

      // SECCIÓN 5: ANÁLISIS (Recuadro libre)
      analysis: '',

      // SECCIÓN 6: DIAGNÓSTICO
      primaryDiagnosis: '',
      primaryDiagnosisCode: null, // Código CIE-10 asociado
      // Lista de diagnósticos secundarios (cantidad ilimitada, se pueden agregar/quitar)
      secondaryDiagnoses: [{ value: '', code: null }],

      // SECCIÓN 7: CONDUCTA
      medications: [emptyMedication()],
      // conduct.procedures (campo viejo): ya no se edita, solo se conserva.
      procedures: [],
      requestedTests: { enabled: false, items: [], notes: '' },
      sickLeave: { enabled: false, startDate: '', days: '', endDate: '', leaveType: '', recommendations: '' },
      recommendations: '',
      followUpRequired: false,
      followUpDate: '',
      followUpInstructions: ''
    };
  }

  const secondaryList = initialData.diagnosis?.secondary || [];
  const secondaryCodeList = initialData.diagnosis?.secondaryCode || [];
  return {
    chiefComplaint: initialData.chiefComplaint || appointment?.reason || '',
    chiefComplaintCode: toCodeObject(initialData.chiefComplaintCode),
    currentIllness: initialData.currentIllness || '',
    pathologicalHistory: initialData.pathologicalHistory || '',
    weight: initialData.physicalExam?.weight?.value || '',
    height: initialData.physicalExam?.height?.value || '',
    bloodPressure: initialData.physicalExam?.bloodPressure 
      ? `${initialData.physicalExam.bloodPressure.systolic}/${initialData.physicalExam.bloodPressure.diastolic}`
      : '',
    heartRate: initialData.physicalExam?.heartRate || '',
    temperature: initialData.physicalExam?.temperature || '',
    oxygenSaturation: initialData.physicalExam?.oxygenSaturation || '',
    physicalExamNotes: initialData.physicalExam?.notes || '',
    // Las historias anteriores a este campo no lo traen: arranca apagada.
    previousLabResults: {
      enabled: initialData.previousLabResults?.enabled === true,
      items: initialData.previousLabResults?.items?.length > 0
        ? initialData.previousLabResults.items.map(item => ({
            name: item.name || '',
            date: fromIsoToDateInput(item.date),
            result: item.result || ''
          }))
        : [emptyLabRow()],
      notes: initialData.previousLabResults?.notes || ''
    },
    analysis: initialData.analysis || '',
    primaryDiagnosis: initialData.diagnosis?.primary || '',
    primaryDiagnosisCode: toCodeObject(initialData.diagnosis?.primaryCode),
    secondaryDiagnoses: secondaryList.length > 0
      ? secondaryList.map((value, idx) => ({
          value: value || '',
          code: toCodeObject(secondaryCodeList[idx])
        }))
      : [{ value: '', code: null }],
    // Copia de cada medicamento: editar el formulario no debe tocar initialData.
    medications: initialData.conduct?.medications?.length > 0
      // Las historias anteriores no traen presentation ni route: sin el || ''
      // el select quedaría sin controlar (undefined) y React avisaría.
      ? initialData.conduct.medications.map(med => ({
          ...med,
          presentation: med.presentation || '',
          route: med.route || ''
        }))
      : [emptyMedication()],
    // Se conserva la lista completa tal como estaba: ya no hay campo para
    // editarla, pero una historia firmada no puede perder procedimientos al
    // guardarla de nuevo (antes solo viajaba el primero).
    procedures: Array.isArray(initialData.conduct?.procedures) ? [...initialData.conduct.procedures] : [],
    requestedTests: {
      enabled: initialData.requestedTests?.enabled === true,
      items: (initialData.requestedTests?.items || []).map(item => ({
        name: item.name || '',
        category: item.category || '',
        priority: item.priority || 'Rutina',
        indication: item.indication || ''
      })),
      notes: initialData.requestedTests?.notes || ''
    },
    // Las fechas vuelven como instantes ISO; el <input type="date"> necesita el
    // día de Bogotá en YYYY-MM-DD. days vuelve numérico y en el estado es texto.
    sickLeave: {
      enabled: initialData.sickLeave?.enabled === true,
      startDate: fromIsoToDateInput(initialData.sickLeave?.startDate),
      days: initialData.sickLeave?.days ? String(initialData.sickLeave.days) : '',
      endDate: fromIsoToDateInput(initialData.sickLeave?.endDate),
      leaveType: initialData.sickLeave?.leaveType || '',
      recommendations: initialData.sickLeave?.recommendations || ''
    },
    recommendations: initialData.conduct?.recommendations || '',
    followUpRequired: initialData.conduct?.followUp?.required || false,
    followUpDate: initialData.conduct?.followUp?.recommendedDate || '',
    followUpInstructions: initialData.conduct?.followUp?.instructions || ''
  };
}

// Fecha del borrador en formato local, p. ej. "23 sept 2026, 10:15 a. m.".
function formatDraftDate(savedAt) {
  try {
    return new Date(savedAt).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return new Date(savedAt).toString();
  }
}

function MedicalRecordForm({ appointment, initialData, userId, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(!!initialData);
  const [savedRecordId, setSavedRecordId] = useState(initialData?._id || null);
  // Lo último que se guardó con éxito: el menú de descarga ofrece los documentos
  // de la historia GUARDADA, no de lo que hay a medio escribir en pantalla.
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const initialFormData = useMemo(
    () => buildFormData(appointment, initialData),
    [appointment, initialData]
  );
  const [formData, setFormData] = useState(initialFormData);

  // Borrador local: si la página se recarga o la sesión redirige a /login a
  // media escritura, lo escrito se puede recuperar al volver a abrir la historia.
  const draft = useHistoriaDraft({
    userId,
    appointmentId: appointment?._id,
    formData,
    initialFormData,
  });

  // Pre-llenar formulario si es modo edición
  useEffect(() => {
    if (initialData && isEditMode) {
      console.log('📄 Datos iniciales cargados:', initialData);
      setFormData(buildFormData(appointment, initialData));
      setSavedRecordId(initialData._id);
    }
  }, [initialData, isEditMode, appointment]);

  // IMC calculado automaticamente en tiempo real a partir de peso y talla
  const computedBmi = (() => {
    const weightKg = parseFloat(formData.weight);
    const heightCm = parseFloat(formData.height);
    if (!weightKg || !heightCm) return null;
    const heightM = heightCm / 100;
    const value = weightKg / (heightM * heightM);
    return isFinite(value) ? parseFloat(value.toFixed(2)) : null;
  })();
  const bmiCategory = getBmiCategory(computedBmi);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Copia el medicamento en lugar de mutarlo: la mutación modificaba el objeto
  // del estado anterior (y, en edición, el de initialData).
  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.map((med, i) =>
        i === index ? { ...med, [field]: value } : med
      )
    }));
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, emptyMedication()]
    }));
  };

  const removeMedication = (index) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index)
    }));
  };

  // Maneja el cambio de un diagnóstico secundario específico (texto + código CIE-10)
  const handleSecondaryDiagnosisChange = (index, value, code) => {
    setFormData(prev => {
      const newDiagnoses = [...prev.secondaryDiagnoses];
      newDiagnoses[index] = { value, code };
      return { ...prev, secondaryDiagnoses: newDiagnoses };
    });
  };

  // Agrega un nuevo campo vacío de diagnóstico secundario
  const addSecondaryDiagnosis = () => {
    setFormData(prev => ({
      ...prev,
      secondaryDiagnoses: [...prev.secondaryDiagnoses, { value: '', code: null }]
    }));
  };

  // Elimina un diagnóstico secundario por índice
  const removeSecondaryDiagnosis = (index) => {
    setFormData(prev => ({
      ...prev,
      secondaryDiagnoses: prev.secondaryDiagnoses.filter((_, i) => i !== index)
    }));
  };

  const handleGeneratePDF = async (doc = 'historia') => {
    if (!savedRecordId) {
      setError('Debes guardar la historia clínica primero para generar el PDF');
      return;
    }

    setGeneratingPDF(true);
    setError('');
    try {
      // generatePDF pide un token de un solo uso y abre el PDF en una pestaña
      // nueva; no devuelve blob. Antes se intentaba createObjectURL(null) aquí
      // y el usuario veía un error en pantalla aunque el PDF sí se abría.
      await medicalRecordService.generatePDF(savedRecordId, doc);
    } catch (err) {
      console.error('❌ Error generando PDF:', err);
      setError(`Error al generar el PDF: ${err.message}`);
    } finally {
      setGeneratingPDF(false);
    }
  };

  // Cerrar con la X o con Cancelar. El borrador se conserva en ambos casos; la
  // confirmación evita cerrar por accidente una historia a medio escribir.
  const handleRequestClose = () => {
    if (draft.dirty) {
      const confirmed = window.confirm(
        'Hay cambios sin guardar en esta historia clínica.\n\n' +
        'Se conservan como borrador en este navegador y podrás recuperarlos al ' +
        'volver a abrirla. ¿Cerrar de todos modos?'
      );
      if (!confirmed) return;
      draft.flush();
    }
    if (onClose) onClose();
  };

  const handleRestoreDraft = () => {
    const saved = draft.restore();
    // Sobre la base actual, por si el borrador es de una versión anterior del
    // formulario y le falta algún campo.
    if (saved) setFormData(prev => ({ ...prev, ...saved }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validación básica
      if (!formData.chiefComplaint.trim()) {
        throw new Error('El motivo de consulta es requerido');
      }
      if (!formData.primaryDiagnosis.trim()) {
        throw new Error('El diagnóstico principal es requerido');
      }

      // Parse blood pressure
      let bloodPressure = {};
      if (formData.bloodPressure) {
        const parts = formData.bloodPressure.split('/').map(n => parseInt(n.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          bloodPressure = { systolic: parts[0], diastolic: parts[1] };
        }
      }

      // IMC y categoria calculados automaticamente (mismo valor mostrado en pantalla)
      const bmi = computedBmi;
      const bmiCategoryLabel = bmiCategory ? bmiCategory.label : null;

      // Filtrar diagnósticos secundarios con texto no vacío
      const validSecondaryDiagnoses = formData.secondaryDiagnoses.filter(d => d.value && d.value.trim());

      const data = {
        appointmentId: appointment._id,

        // SECCIÓN 1: MOTIVO DE CONSULTA (con código CIE-10 opcional)
        chiefComplaint: formData.chiefComplaint,
        chiefComplaintCode: formData.chiefComplaintCode?.code || null,

        // SECCIÓN 2: ENFERMEDAD ACTUAL
        currentIllness: formData.currentIllness,

        // SECCIÓN 3: ANTECEDENTES
        pathologicalHistory: formData.pathologicalHistory,

        // SECCIÓN 4: EXAMEN FÍSICO
        physicalExam: {
          weight: formData.weight ? { value: parseFloat(formData.weight), unit: 'kg' } : null,
          height: formData.height ? { value: parseFloat(formData.height), unit: 'cm' } : null,
          bmi: bmi,
          bmiCategory: bmiCategoryLabel,
          bloodPressure: Object.keys(bloodPressure).length > 0 ? bloodPressure : null,
          heartRate: formData.heartRate ? parseInt(formData.heartRate) : null,
          temperature: formData.temperature ? parseFloat(formData.temperature) : null,
          oxygenSaturation: formData.oxygenSaturation ? parseFloat(formData.oxygenSaturation) : null,
          notes: formData.physicalExamNotes
        },

        // RESULTADOS DE EXÁMENES PREVIOS. Se envía aunque esté apagada: apagar la
        // casilla no borra lo escrito, solo evita que se imprima.
        previousLabResults: {
          enabled: formData.previousLabResults.enabled,
          items: formData.previousLabResults.items
            .filter(item => item.name.trim())
            .map(item => ({ ...item, date: toBogotaIso(item.date) })),
          notes: formData.previousLabResults.notes
        },

        // SECCIÓN 5: ANÁLISIS
        analysis: formData.analysis,

        // SECCIÓN 6: DIAGNÓSTICO (con códigos CIE-10). Ahora soporta múltiples diagnósticos secundarios.
        diagnosis: {
          primary: formData.primaryDiagnosis,
          primaryCode: formData.primaryDiagnosisCode?.code || null,
          secondary: validSecondaryDiagnoses.map(d => d.value),
          secondaryCode: validSecondaryDiagnoses.map(d => d.code?.code || null)
        },

        // SECCIÓN 7: CONDUCTA
        conduct: {
          medications: formData.medications.filter(m => m.name.trim()),
          // Un borrador local antiguo puede traer el texto del campo viejo como string.
          procedures: Array.isArray(formData.procedures)
            ? formData.procedures
            : (formData.procedures ? [formData.procedures] : []),
          recommendations: formData.recommendations,
          followUp: {
            required: formData.followUpRequired,
            recommendedDate: formData.followUpRequired ? formData.followUpDate : null,
            instructions: formData.followUpInstructions
          }
        },

        // EXÁMENES Y PROCEDIMIENTOS SOLICITADOS (misma regla: apagar no borra)
        requestedTests: {
          enabled: formData.requestedTests.enabled,
          items: formData.requestedTests.items.filter(item => item.name.trim()),
          notes: formData.requestedTests.notes
        },

        // INCAPACIDAD MÉDICA. days DEBE viajar como número: el backend responde
        // 400 si no es un entero, y el input entrega una cadena ("3"). Vacío se
        // manda undefined (no 0) para que el error diga "faltan los días".
        sickLeave: {
          enabled: formData.sickLeave.enabled,
          startDate: toBogotaIso(formData.sickLeave.startDate),
          days: sickLeaveDays(formData.sickLeave),
          endDate: toBogotaIso(formData.sickLeave.endDate),
          leaveType: formData.sickLeave.leaveType || undefined,
          recommendations: formData.sickLeave.recommendations
        }
      };

      if (isEditMode && savedRecordId) {
        // Modo EDICIÓN
        console.log(`📄 Actualizando historia con ID: ${savedRecordId}`);
        const response = await medicalRecordService.updateMedicalRecord(savedRecordId, data);
        console.log('✅ Historia actualizada:', response);
        alert('✅ Historia clínica actualizada exitosamente');
      } else {
        // Modo CREACIÓN
        console.log('📄 Creando nueva historia...');
        const response = await medicalRecordService.createMedicalRecord(data);

        let recordId = null;
        if (response?.data?._id) {
          recordId = response.data._id;
        } else if (response?._id) {
          recordId = response._id;
        }

        if (recordId) {
          setSavedRecordId(recordId);
          console.log(`✅ Medical record creado con ID: ${recordId}`);
          setIsEditMode(true);
          alert('✅ Historia clínica creada exitosamente');
        } else {
          console.warn('⚠️ Medical record creado pero sin ID en respuesta');
          setError('La historia se creó, pero no se pudo obtener el ID');
        }
      }

      // Ya está en el servidor: el borrador local sobra.
      draft.markSaved();
      setSavedSnapshot(data);

      if (onSuccess) onSuccess();
      if (onClose) onClose();

    } catch (err) {
      console.error('❌ Error:', err);
      const message = err.response?.data?.message || err.message || 'Error al guardar historia clínica';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-white rounded-t-lg flex-shrink-0">
          <div className="flex items-center">
            <FileText className="h-6 w-6 text-primary-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Historia Clínica</h2>
              <p className="text-sm text-gray-600">
                Paciente: {appointment?.patient?.user?.profile?.firstName} {appointment?.patient?.user?.profile?.lastName}
                {isEditMode && <span className="ml-2 text-blue-600 font-semibold">(Modo Edición)</span>}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleRequestClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form con scroll */}
        <div className="flex-1 overflow-y-auto p-6">
          {draft.pendingDraft && (
            <div
              role="status"
              className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <p className="text-sm text-yellow-900">
                Hay un borrador sin guardar de{' '}
                <strong>{formatDraftDate(draft.pendingDraft.savedAt)}</strong>.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handleRestoreDraft}
                  className="px-3 py-1.5 text-sm font-medium bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                >
                  Recuperar
                </button>
                <button
                  type="button"
                  onClick={draft.discard}
                  className="px-3 py-1.5 text-sm font-medium border border-yellow-400 text-yellow-900 rounded-lg hover:bg-yellow-100"
                >
                  Descartar
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start mb-6">
              <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-6">

            {/* SECCIÓN 1: MOTIVO DE CONSULTA con CIE-10 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">1. MOTIVO DE CONSULTA *</h3>
              <Cie10Autocomplete
                value={formData.chiefComplaint}
                onChange={(value, code) => {
                  setFormData(prev => ({
                    ...prev,
                    chiefComplaint: value,
                    chiefComplaintCode: code
                  }));
                }}
                placeholder="Buscar síntoma en CIE-10 o escribir manualmente..."
                type="symptom"
                label=""
                required
              />
            </div>

            {/* SECCIÓN 2: ENFERMEDAD ACTUAL */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">2. ENFERMEDAD ACTUAL</h3>
              <textarea
                name="currentIllness"
                value={formData.currentIllness}
                onChange={handleChange}
                rows="3"
                className="input-field"
                placeholder="Historia de enfermedad actual, síntomas, duración, evolución..."
              />
            </div>

            {/* SECCIÓN 3: ANTECEDENTES */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">3. ANTECEDENTES</h3>
              <textarea
                name="pathologicalHistory"
                value={formData.pathologicalHistory}
                onChange={handleChange}
                rows="3"
                className="input-field"
                placeholder="Enfermedades previas, cirugías, alergias, medicamentos actuales..."
              />
            </div>

            {/* SECCIÓN 4: EXAMEN FÍSICO */}
            <div className="border-l-4 border-red-500 pl-4">
              <div className="flex items-center mb-4">
                <Activity className="h-5 w-5 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">4. EXAMEN FÍSICO</h3>
              </div>

              {/* Signos Vitales */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-800 mb-4">Signos Vitales</h4>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Peso (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="weight"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="70"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Talla (cm)</label>
                    <input
                      type="number"
                      name="height"
                      value={formData.height}
                      onChange={handleChange}
                      placeholder="170"
                      className="input-field"
                    />
                  </div>
                </div>

                {/* IMC calculado automaticamente con indicador de rango */}
                {computedBmi !== null && (
                  <div className={`rounded-lg border p-3 mb-4 ${bmiCategory.color}`}>
                    <p className="text-sm font-semibold">
                      IMC calculado: {computedBmi} kg/m² — {bmiCategory.label}
                    </p>
                  </div>
                )}

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Presión (mmHg)</label>
                    <input
                      type="text"
                      name="bloodPressure"
                      value={formData.bloodPressure}
                      onChange={handleChange}
                      placeholder="120/80"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Frecuencia (bpm)</label>
                    <input
                      type="number"
                      name="heartRate"
                      value={formData.heartRate}
                      onChange={handleChange}
                      placeholder="75"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Temperatura (°C)</label>
                    <input
                      type="number"
                      step="0.1"
                      name="temperature"
                      value={formData.temperature}
                      onChange={handleChange}
                      placeholder="36.5"
                      className="input-field"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">O2 Sat (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="oxygenSaturation"
                    value={formData.oxygenSaturation}
                    onChange={handleChange}
                    placeholder="98"
                    className="input-field"
                  />
                </div>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-2">Observaciones del Examen Físico</label>
              <textarea
                name="physicalExamNotes"
                value={formData.physicalExamNotes}
                onChange={handleChange}
                rows="4"
                className="input-field"
                placeholder="Descripción detallada del examen físico realizado..."
              />
            </div>

            {/* RESULTADOS DE EXÁMENES PREVIOS (opcional) */}
            <PreviousLabResultsSection
              value={formData.previousLabResults}
              onChange={(next) => setFormData(prev => ({ ...prev, previousLabResults: next }))}
            />

            {/* SECCIÓN 5: ANÁLISIS */}
            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">5. ANÁLISIS</h3>
              <textarea
                name="analysis"
                value={formData.analysis}
                onChange={handleChange}
                rows="4"
                className="input-field"
                placeholder="Análisis de los hallazgos, interpretación de pruebas, conclusiones clínicas..."
              />
            </div>

            {/* SECCIÓN 6: DIAGNÓSTICO con CIE-10 */}
            <div className="border-l-4 border-indigo-500 pl-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">6. DIAGNÓSTICO</h3>
              <div className="space-y-4">
                <Cie10Autocomplete
                  value={formData.primaryDiagnosis}
                  onChange={(value, code) => {
                    setFormData(prev => ({
                      ...prev,
                      primaryDiagnosis: value,
                      primaryDiagnosisCode: code
                    }));
                  }}
                  placeholder="Buscar diagnóstico en CIE-10 o escribir manualmente..."
                  type="diagnosis"
                  label="Diagnóstico Principal *"
                  required
                />

                {/* Diagnósticos secundarios (múltiples, se pueden agregar o quitar) */}
                <div className="space-y-3">
                  {formData.secondaryDiagnoses.map((diag, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-1">
                        <Cie10Autocomplete
                          value={diag.value}
                          onChange={(value, code) => handleSecondaryDiagnosisChange(index, value, code)}
                          placeholder="Buscar diagnóstico secundario en CIE-10 o escribir manualmente..."
                          type="diagnosis"
                          label={`Diagnóstico Secundario ${index + 1} (opcional)`}
                        />
                      </div>
                      {formData.secondaryDiagnoses.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSecondaryDiagnosis(index)}
                          className="btn-secondary text-red-600 border-red-300 hover:bg-red-50 text-sm mt-7"
                        >
                          Eliminar
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addSecondaryDiagnosis}
                    className="btn-secondary text-sm"
                  >
                    + Agregar diagnóstico secundario
                  </button>
                </div>
              </div>
            </div>

            {/* SECCIÓN 7: CONDUCTA */}
            <div className="border-l-4 border-pink-500 pl-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Pill className="h-5 w-5 text-pink-600 mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">7. CONDUCTA</h3>
                </div>
              </div>

              {/* Medicamentos */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-800">Medicamentos</h4>
                  <button
                    type="button"
                    onClick={addMedication}
                    className="btn-secondary text-sm"
                  >
                    + Agregar
                  </button>
                </div>
                {formData.medications.map((med, index) => (
                  <div key={index} className="border rounded-lg p-4 mb-3 bg-gray-50">
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Medicamento</label>
                        <input
                          type="text"
                          value={med.name}
                          onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                          placeholder="Ej: Ibuprofeno"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Dosis</label>
                        <input
                          type="text"
                          value={med.dosage}
                          onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                          placeholder="400mg"
                          className="input-field"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <label htmlFor={`presentation-${index}`} className="block text-xs font-medium text-gray-700 mb-1">Presentación</label>
                        <select
                          id={`presentation-${index}`}
                          value={med.presentation || ''}
                          onChange={(e) => handleMedicationChange(index, 'presentation', e.target.value)}
                          className="input-field"
                        >
                          <option value="">Sin especificar</option>
                          {PRESENTATIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`route-${index}`} className="block text-xs font-medium text-gray-700 mb-1">Vía</label>
                        <select
                          id={`route-${index}`}
                          value={med.route || ''}
                          onChange={(e) => handleMedicationChange(index, 'route', e.target.value)}
                          className="input-field"
                        >
                          <option value="">Sin especificar</option>
                          {ROUTES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Frecuencia</label>
                        <input
                          type="text"
                          value={med.frequency}
                          onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                          placeholder="cada 8h"
                          className="input-field"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Duración</label>
                        <input
                          type="text"
                          value={med.duration}
                          onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                          placeholder="7 días"
                          className="input-field"
                        />
                      </div>
                      {formData.medications.length > 1 && (
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeMedication(index)}
                            className="btn-secondary w-full text-red-600 border-red-300 hover:bg-red-50 text-sm"
                          >
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Recomendaciones */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Recomendaciones / Instrucciones</label>
                <textarea
                  name="recommendations"
                  value={formData.recommendations}
                  onChange={handleChange}
                  rows="3"
                  className="input-field"
                  placeholder="Recomendaciones generales e instrucciones para el paciente..."
                />
              </div>

              {/* Seguimiento */}
              <div className="border-t pt-4">
                <label className="flex items-center mb-4">
                  <input
                    type="checkbox"
                    name="followUpRequired"
                    checked={formData.followUpRequired}
                    onChange={handleChange}
                    className="mr-2"
                  />
                  <span className="font-medium text-gray-700">Requiere seguimiento</span>
                </label>

                {formData.followUpRequired && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de Seguimiento</label>
                      <input
                        type="date"
                        name="followUpDate"
                        value={formData.followUpDate}
                        onChange={handleChange}
                        min={new Date().toISOString().split('T')[0]}
                        className="input-field max-w-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Instrucciones de Seguimiento</label>
                      <textarea
                        name="followUpInstructions"
                        value={formData.followUpInstructions}
                        onChange={handleChange}
                        rows="2"
                        className="input-field"
                        placeholder="Instrucciones específicas para el seguimiento..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* EXÁMENES Y PROCEDIMIENTOS SOLICITADOS (opcional) */}
            <RequestedTestsSection
              value={formData.requestedTests}
              onChange={(next) => setFormData(prev => ({ ...prev, requestedTests: next }))}
            />

            {/* INCAPACIDAD MÉDICA (opcional) */}
            <SickLeaveSection
              value={formData.sickLeave}
              onChange={(next) => setFormData(prev => ({ ...prev, sickLeave: next }))}
            />

          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg flex-shrink-0">
          <button
            type="button"
            onClick={handleRequestClose}
            className="btn-secondary"
            disabled={loading || generatingPDF}
          >
            Cancelar
          </button>

          {isEditMode && (
            <DocumentDownloadMenu
              record={savedSnapshot || initialData}
              onDownload={handleGeneratePDF}
              busy={generatingPDF}
              disabled={loading}
              label="Generar PDF"
              busyLabel="Generando PDF..."
              buttonClassName="btn-secondary flex items-center bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
            />
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || generatingPDF}
            className="btn-primary flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Guardando...
              </>
            ) : isEditMode ? (
              <>
                <Save className="h-5 w-5 mr-2" />
                Actualizar Historia
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Guardar Historia
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MedicalRecordForm;
